import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { normalizeCatalogCriteriaComponents } from "../../../utils/transformCompleteCatalogToEditor";

const CATALOG_CACHE_KEY = "acr_editor_catalog_cache";

/** Fallback when catalog id is not in the cache (e.g. before API load). */
const EMPTY_CATALOG = {
  standards: [],
  chapters: [],
  components: [],
  conformanceLevels: [],
};

function getCatalogCache() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(CATALOG_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function setCatalogCache(map) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify(map));
  } catch {
    // ignore quota or other errors
  }
}

export const CatalogContext = React.createContext({
  /** Map of catalog id -> catalog object (from API). */
  catalogDataMap: {},
  setCatalogData: () => {},
  /** Resolve catalog by id: from API map or empty fallback. */
  getCatalog: () => ({}),
});

export function CatalogProvider({ children }) {
  const [catalogDataMap, setCatalogDataMap] = useState(getCatalogCache);

  const setCatalogData = useCallback((id, data) => {
    setCatalogDataMap((prev) => {
      const next = { ...prev };
      if (data) next[id] = data;
      else delete next[id];
      return next;
    });
  }, []);

  // Persist catalog map to localStorage when it changes so refresh can restore without calling the API
  useEffect(() => {
    setCatalogCache(catalogDataMap);
  }, [catalogDataMap]);

  const getCatalog = useCallback(
    (catalogId) => {
      const catalog = catalogDataMap[catalogId];
      if (!catalog) return EMPTY_CATALOG;
      return normalizeCatalogCriteriaComponents(catalog) ?? catalog;
    },
    [catalogDataMap]
  );

  const value = useMemo(
    () => ({ catalogDataMap, setCatalogData, getCatalog }),
    [catalogDataMap, setCatalogData, getCatalog]
  );

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

CatalogProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default CatalogContext;
